import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { _Api } from "../../api/_call";
import { error, loading, Updateloading } from "../../common/helpers/toastify";
import { LocationList, VoucherList } from "../../common/graphql/commonlayouts";
import { toFormatPrice } from "../../common/helpers/function";
import NotesModalLabel from "../../common/modules/NotesModalLabel";
import ConfirmationModel from "../../common/modules/confirmationmodel";
import TableLoader from "../../../admin/common/TableLoader";
import { useTranslation } from "react-i18next";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

/* -----------------------------------------------------------------------------
 * Create / Edit Stock Adjustment.
 *
 * Create mode: receives the products selected on createSAInventory.js
 * (location.state.createlist) and lets the user set an Adjust Qty per line.
 *   prepare : api/v1/Inventory/stockAdjustment/prepare
 *             body { po_product_ids: [...], location_id }
 *   create  : api/v1/Inventory/stockAdjustment/create  (PLACEHOLDER)
 *
 * Edit mode: entered by clicking a pending SA's SA No. in stock_adjustment.js,
 * which passes location.state.adjustmentid. Loads the existing adjustment via
 * getSADetails first, then lets the user re-adjust or remove lines.
 *   details : api/v1/Inventory/stockAdjustment/getSADetails
 *             body { adjustment_id }
 *   edit    : api/v1/Inventory/stockAdjustment/edit
 *             body { adjustment_id, lines: [{ line_id, adjust_qty }],
 *                     deleted_adjustment_ids: [line_id, ...],
 *                     new_adjustment_ids: [{ po_product_id, adjust_qty, reason, remark }] }
 * ---------------------------------------------------------------------------*/
const PREPARE_ENDPOINT = "api/v1/Inventory/stockAdjustment/prepare";
const CREATE_ENDPOINT = "api/v1/Inventory/stockAdjustment/create";
const GET_SA_DETAILS_ENDPOINT = "api/v1/Inventory/stockAdjustment/getSADetails";
const EDIT_ENDPOINT = "api/v1/Inventory/stockAdjustment/edit";

const HEAD_COLUMNS = [
  { key: "product_name", label: "Product Name", sortable: true },
  { key: "SKU", label: "SKU", sortable: true },
  { key: "stock_id", label: "Stock ID", sortable: true },
  { key: "metal_name", label: "Metal", sortable: true },
  { key: "stone_name", label: "Stone", sortable: true },
  { key: "size_name", label: "Size", sortable: true },
  { key: "qty", label: "Qty", sortable: true },
  { key: "adjust_qty", label: "Adj Qty", sortable: true },
  { key: "price", label: "Price", sortable: true },
  { key: "amount", label: "Amount", sortable: true },
  { key: "location", label: "Location", sortable: true },
];

// `price` comes back as a plain number from /prepare, so it can be used
// directly — no toUnformatPrice() round-trip needed.
const rowAmount = (row) =>
  (Number(row?.price) || 0) * (parseInt(row?.adjust_qty || 0, 10) || 0);

const SORT_ACCESSORS = {
  product_name: (r) => r?.product_name,
  SKU: (r) => r?.SKU,
  stock_id: (r) => r?.stock_id,
  metal_name: (r) => r?.metal_name,
  stone_name: (r) => r?.stone_name,
  size_name: (r) => r?.size_name,
  qty: (r) => Number(r?.qty) || 0,
  adjust_qty: (r) => Number(r?.adjust_qty) || 0,
  price: (r) => Number(r?.price) || 0,
  amount: (r) => rowAmount(r),
  location: (r) => r?.location,
};

const CreateStockAdjustment = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const base_assets = process.env.REACT_APP_BASE_ASSETS_URL;

  // Edit mode is entered via the SA No. link on a pending row (stock_adjustment.js),
  // which passes { adjustmentid }. Create mode passes { createlist } instead.
  const isEditMode = Boolean(state?.adjustmentid);

  const [limit, setLimit] = useState(100);
  const [filtername, setFiltername] = useState("");
  const [form_note, setForm_note] = useState(null);
  const [commanSelected_vouc, setcommanSelected_vouc] = useState(null);
  const [commanSelected_loc, setcommanSelected_loc] = useState(null);
  const [editLocationId, setEditLocationId] = useState("");
  const [transactionDate, setTransactionDate] = useState(new Date());
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [adjNo, setAdjNo] = useState("");
  const [listdetail, setListdetail] = useState([]);
  const [deletedLineIds, setDeletedLineIds] = useState([]);
  const [selecteditem, setSelecteditem] = useState([]);
  const [datalodaer, setDatsLodaer] = useState(false);
  const [ShowSubmitbutton, setShowSubmitbutton] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [confirmStatement, setConfirmStatement] = useState("");

  const selectOptions = [
    { label: 100, value: 100 },
    { label: 200, value: 200 },
    { label: 300, value: 300 },
  ];

  // Edit mode: load the existing adjustment first.
  const loadForEdit = async () => {
    setDatsLodaer(true);
    try {
      let res = await _Api(
        { adjustment_id: state.adjustmentid },
        GET_SA_DETAILS_ENDPOINT
      );
      if (res?.code === 200) {
        const data = res.data || {};
        setAdjNo(data.voucher_no || "");
        setAdjustmentReason(data.reason || "");
        setForm_note(data.remark || "");
        setEditLocationId(data.location_id || "");
        if (data.transaction_date) {
          setTransactionDate(new Date(data.transaction_date));
        }
        const lines = Array.isArray(data.lines) ? data.lines : [];
        const seeded = lines.map((item, index) => ({
          ...item,
          qty: item.total_qty_at_create ?? item.qty ?? 0,
          adjust_qty: item.adjust_qty ?? 0,
          // Cap edits at the API's max_qty (falls back to the originally
          // committed qty if max_qty isn't present on a line).
          max_adj_qty: item.max_qty ?? item.total_qty_at_create ?? item.qty ?? 0,
          location: data.location_name,
          _rowKey: item.line_id || `row_${index}`,
        }));
        setListdetail(seeded);
      } else {
        error(res?.message || t("Something went wrong"));
        setListdetail([]);
      }
    } catch (err) {
      console.log(err, "err");
    } finally {
      setDatsLodaer(false);
    }
  };

  // Create mode: prepare the products selected on createSAInventory.js.
  const submitcreatelist = async () => {
    if (!state?.createlist?.length) return;
    setDatsLodaer(true);
    try {
      const postdata = {
        po_product_ids: state.createlist,
        location_id: state.hidelocationid,
      };
      let res = await _Api(postdata, PREPARE_ENDPOINT);
      if (res.code === 200) {
        // Seed each line with an editable adjust_qty (defaults to the API's
        // adj_qty) and a _rowKey unique per row even if po_product_id repeats
        // across lines (e.g. same product, different stock_id) — po_product_id
        // alone isn't safe to key row-level UI state off of.
        const seeded = (res.data || []).map((item, index) => ({
          ...item,
          adjust_qty: item.adj_qty ?? item.qty ?? 0,
          _rowKey: `${item.po_product_id || "row"}_${item.stock_id || index}_${index}`,
        }));
        setListdetail(seeded);
        setDatsLodaer(false);
      } else {
        setListdetail([]);
        setDatsLodaer(false);
      }
    } catch (err) {
      console.log(err, "err");
      setDatsLodaer(false);
    }
  };

  useEffect(() => {
    if (isEditMode) {
      loadForEdit();
    } else {
      submitcreatelist();
    }
    // eslint-disable-next-line
  }, []);

  // Search filters the already-prepared list client-side.
  const filterbyname = (search) => setFiltername(search);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedList = useMemo(() => {
    const term = (filtername || "").trim().toLowerCase();
    const base = term
      ? listdetail.filter((r) =>
          [r?.product_name, r?.SKU, r?.stock_id].some((v) =>
            String(v || "")
              .toLowerCase()
              .includes(term)
          )
        )
      : listdetail;
    const accessor = SORT_ACCESSORS[sortConfig.key];
    if (!accessor) return base;
    const sorted = [...base].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv), undefined, { numeric: true });
    });
    return sortConfig.direction === "asc" ? sorted : sorted.reverse();
  }, [listdetail, sortConfig, filtername]);

  const changeAdjQty = (rowKey, rawValue) => {
    // Strip a stale leading zero (e.g. focus-clear racing a fast keystroke
    // can leave "0" + "1" = "01") so typing "10" always lands on 10, not 0/1.
    const value = rawValue.replace(/^0+(?=\d)/, "");
    setListdetail((prev) =>
      prev.map((item) => {
        if (item._rowKey !== rowKey) return item;
        const max =
          parseInt(item.max_adj_qty ?? item.qty ?? 0, 10) || 0;
        let next = value === "" ? "" : parseInt(value, 10);
        if (Number.isNaN(next)) next = "";
        if (next !== "" && next > max) {
          error(t("Enter Quantity less than") + " " + (max + 1));
          next = max;
        }
        if (next !== "" && next < 0) next = 0;
        return { ...item, adjust_qty: next };
      })
    );
    // A row edited down to 0 can no longer be selected — drop it if checked.
    const edited = listdetail.find((i) => i._rowKey === rowKey);
    const max = parseInt(edited?.max_adj_qty ?? edited?.qty ?? 0, 10) || 0;
    const nextVal = value === "" ? "" : Math.min(parseInt(value, 10) || 0, max);
    if (!nextVal) {
      setSelecteditem((prev) => prev.filter((x) => x !== rowKey));
    }
  };

  const isRowSelectable = (item) =>
    item?.adjustable !== false && (parseInt(item?.adjust_qty, 10) || 0) > 0;

  // Clear a lingering "0" on focus so the user can type straight in
  // instead of having to backspace it first.
  const clearIfZero = (rowKey) => {
    setListdetail((prev) =>
      prev.map((item) =>
        item._rowKey === rowKey && (parseInt(item.adjust_qty, 10) || 0) === 0
          ? { ...item, adjust_qty: "" }
          : item
      )
    );
  };

  const toggleSelect = (rowKey) => {
    const item = listdetail.find((i) => i._rowKey === rowKey);
    if (!isRowSelectable(item)) return;
    setSelecteditem((prev) =>
      prev.includes(rowKey)
        ? prev.filter((x) => x !== rowKey)
        : [...prev, rowKey]
    );
  };

  const toggleSelectAll = () => {
    const selectableKeys = listdetail
      .filter(isRowSelectable)
      .map((i) => i._rowKey);
    setSelecteditem((prev) =>
      prev.length === selectableKeys.length ? [] : selectableKeys
    );
  };

  const deleteProduct = () => {
    // In edit mode, removing an existing (already-saved) line must be
    // reported to the API as a deletion, not silently dropped.
    const existingLineIds = listdetail
      .filter((i) => selecteditem.includes(i._rowKey) && i.line_id)
      .map((i) => i.line_id);
    if (existingLineIds.length) {
      setDeletedLineIds((prev) => [...prev, ...existingLineIds]);
    }
    setListdetail((prev) =>
      prev.filter((i) => !selecteditem.includes(i._rowKey))
    );
    setSelecteditem([]);
  };

  const totalQty = listdetail.reduce(
    (s, v) => s + (parseInt(v.qty || 0, 10) || 0),
    0
  );
  const totalAdjQty = listdetail.reduce(
    (s, v) => s + (parseInt(v.adjust_qty || 0, 10) || 0),
    0
  );
  const totalAmount = listdetail.reduce((s, v) => s + rowAmount(v), 0);

  // Validate, then ask for confirmation before actually calling the
  // create/edit API (statement text differs per mode).
  const submitdata = () => {
    if (!commanSelected_loc) {
      error(t("Please select location"));
      return;
    }
    if (!transactionDate) {
      error(t("Please select Transaction Date"));
      return;
    }
    // In create mode you must pick at least one item. In edit mode an empty
    // list is a valid state (e.g. every line was removed) — not an error.
    if (!isEditMode && !listdetail.length) {
      error(t("No item Selected!!"));
      return;
    }
    const invalid = listdetail.some(
      (i) => !i.adjust_qty || parseInt(i.adjust_qty, 10) <= 0
    );
    if (invalid) {
      error(t("Enter a valid Adjust Qty for every item"));
      return;
    }

    setConfirmStatement(
      isEditMode
        ? t("update this stock adjustment")
        : t("create this stock adjustment")
    );
    document.querySelector("#status_query_select").click();
  };

  const doSubmit = async () => {
    setShowSubmitbutton(false);
    let GetloadingID = loading();
    try {
      let res;
      if (isEditMode) {
        const payload = {
          adjustment_id: state.adjustmentid,
          lines: listdetail
            .filter((it) => it.line_id)
            .map((it) => ({
              line_id: it.line_id,
              adjust_qty: parseInt(it.adjust_qty || 0, 10) || 0,
            })),
          deleted_adjustment_ids: deletedLineIds,
          new_adjustment_ids: listdetail
            .filter((it) => !it.line_id)
            .map((it) => ({
              po_product_id: it.po_product_id,
              adjust_qty: parseInt(it.adjust_qty || 0, 10) || 0,
              reason: it.reason?.trim() || (adjustmentReason || "").trim(),
              remark: it.remark || "",
            })),
        };
        res = await _Api(payload, EDIT_ENDPOINT);
      } else {
        const postdata = {
          voucher_id: commanSelected_vouc,
          // Sent as a UTC ISO string (e.g. "2026-07-06T00:00:00.000Z").
          transaction_date: new Date(transactionDate).toISOString(),
          location_id: commanSelected_loc,
          reason: (adjustmentReason || "").trim(),
          remark: form_note,
          lines: listdetail.map((it) => ({
            po_product_id: it.po_product_id,
            adjust_qty: parseInt(it.adjust_qty || 0, 10) || 0,
            reason: it.reason?.trim() || (adjustmentReason || "").trim(),
            remark: it.remark || "",
          })),
        };
        res = await _Api(postdata, CREATE_ENDPOINT);
      }
      if (res.code === 200) {
        setShowSubmitbutton(true);
        Updateloading(
          t,
          GetloadingID,
          t(isEditMode ? "Updated successfully!!" : "Created successfully!!")
        );
        setTimeout(() => navigate("/app/inventory/stockAdjustment"), 100);
      } else {
        setShowSubmitbutton(true);
        Updateloading(
          t,
          GetloadingID,
          res?.errors ? res?.errors : res?.message,
          "error"
        );
      }
    } catch (err) {
      console.log(err, "err");
      setShowSubmitbutton(true);
    }
  };

  const downloadexclsheet = async () => {
    const header_values = [
      "#",
      "Product Name",
      "SKU",
      "Stock ID",
      "Metal",
      "Stone",
      "Size",
      "Qty",
      "Adj Qty",
      "Price",
      "Amount",
      "Location",
    ];
    const bodyarray = listdetail.map((r, key) => [
      key + 1,
      r?.product_name,
      r?.SKU,
      r?.stock_id,
      r?.metal_name,
      r?.stone_name,
      r?.size_name,
      r?.qty,
      r?.adjust_qty,
      r?.price_formatted || toFormatPrice(r?.price, { addSymbol: true }),
      toFormatPrice(rowAmount(r), { addSymbol: true }),
      r?.location,
    ]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(bodyarray, {
      origin: "A2",
      skipHeader: true,
    });
    XLSX.utils.sheet_add_aoa(ws, [header_values], { origin: "A1" });
    XLSX.utils.book_append_sheet(wb, ws, "Records");
    const fileContent = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([fileContent], {
      type: "application/octet-stream",
    });
    FileSaver.saveAs(data, "create_stock_adjustment.xlsx");
  };

  const selectableCount = listdetail.filter(isRowSelectable).length;
  const allSelected =
    selectableCount > 0 && selecteditem.length === selectableCount;

  return (
    <React.Fragment>
      <div className="main-body">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="top-heading-area">
                <div className="top-heading-area-left">
                  <Link
                    to={
                      isEditMode
                        ? "/app/inventory/stockAdjustment"
                        : "/app/inventory/createSAInventory"
                    }
                    className="page-back-btn"
                  >
                    <img
                      src={base_assets + "images/icons/arrow-left.png"}
                      alt=""
                    />
                  </Link>
                  <img src={base_assets + "images/icons/home-icon.png"} alt="" />
                  <VoucherList
                    setcommanSelected_vouc={setcommanSelected_vouc}
                    group={"Stock_Adjustment"}
                    voucherid={state?.Voucher_id}
                  ></VoucherList>
                </div>
                <h1 className="main-heading text-center">
                  {t(isEditMode ? "Edit Stock Adjustment" : "Create Stock Adjustment")}
                </h1>
              </div>

              <div className="main-body-top-tab-bar create-purchase-top-bar">
                <div className="main-body-top-tab-bar-left-col nav nav-pills"></div>
              </div>

              <div className="main-body-top-inputs-bar">
                <div className="main-body-top-inputs-bar-col-left">
                  <label className="po-num-label po-num-red">
                    {isEditMode ? adjNo || t("SA-XXX") : t("SA-XXX")}
                  </label>
                  <LocationList
                    addclass={"row_inactive"}
                    setcommanSelected_loc={setcommanSelected_loc}
                    locationid={
                      isEditMode
                        ? editLocationId
                        : state
                          ? state?.hidelocationid
                          : ""
                    }
                    commanSelected_vouc={commanSelected_vouc}
                  />
                  <div className="placeholder-up-input placeholder-up-input-date">
                    <DatePicker
                      className="placeholder-up-input-field"
                      selected={transactionDate}
                      onChange={(date) => setTransactionDate(date)}
                      name="TransactionDate"
                      dateFormat="dd/MM/yyyy"
                      placeholderText={"DD/MM/YYYY"}
                      autoComplete="off"
                    />
                    <img
                      className="placeholder-up-input-field-calender"
                      src={base_assets + "images/icons/calendar-green.svg"}
                      alt=""
                    />
                    <label className="placeholder-up-input-label">
                      {t("Transaction Date")}
                    </label>
                  </div>
                </div>
                <div className="main-body-top-inputs-bar-col-right">
                  <button
                    onClick={submitdata}
                    className={
                      ShowSubmitbutton
                        ? "main-body-top-inputs-bar-col-right-submit-btn com-btn "
                        : "main-body-top-inputs-bar-col-right-submit-btn com-btn d-block deactive"
                    }
                  >
                    {t(isEditMode ? "Update" : "Create")}
                  </button>
                </div>
              </div>

              <div className="main-body-top-status-bar">
                <Select
                  className="limit-select short mx-3 z-10"
                  options={selectOptions}
                  value={{ label: limit, value: limit }}
                  onChange={(e) => setLimit(e.value)}
                  isSearchable={false}
                />
                <div className="main-body-top-status-bar-filter">
                  <input
                    type="text"
                    value={filtername}
                    className="main-body-top-status-bar-filter-input"
                    placeholder={t("StockID/SKU")}
                    onKeyPress={(e) =>
                      e.key === "Enter" && filterbyname(e.target.value)
                    }
                    onChange={(e) => setFiltername(e.target.value)}
                  />
                  {filtername ? (
                    <img
                      className="main-body-top-status-bar-filter-clear"
                      src={base_assets + "images/icons/False.png"}
                      onClick={() => {
                        setFiltername("");
                        filterbyname("");
                      }}
                      alt=""
                    />
                  ) : (
                    ""
                  )}
                  <button
                    onClick={() => filterbyname(filtername)}
                    className="main-body-top-status-bar-filter-search"
                  >
                    <i className="fa fa-search" aria-hidden="true" />
                  </button>
                </div>
                <div className="main-body-top-status-bar-multi-date-picker">
                  <div className="print-export-dropdown-selected-icon">
                    <button
                      className={
                        selecteditem.length > 0
                          ? "deletebtn btn-style d-block"
                          : "d-none"
                      }
                      onClick={deleteProduct}
                    >
                      <img
                        src={base_assets + "images/icons/delete-icon.png"}
                        alt=""
                      />
                    </button>
                  </div>
                  <div
                    className="main-body-top-status-bar-note-button"
                    data-bs-toggle="modal"
                    data-bs-target="#notesModal"
                  >
                    <img
                      src={base_assets + "images/icons/comment-icon.png"}
                      alt=""
                    />
                    {t("note")}
                  </div>
                  <div className="main-body-top-status-bar-print-export-dropdown dropdown">
                    <button
                      className="btn dropdown-toggle"
                      type="button"
                      id="dropdownMenuButton1"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <img
                        src={base_assets + "images/icons/ellipsis-circular.png"}
                        alt=""
                      />
                    </button>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby="dropdownMenuButton1"
                    >
                      <li
                        className="dropdown-item"
                        onClick={() => downloadexclsheet()}
                      >
                        <div className="dropdown-item-img">
                          <img
                            src={base_assets + "images/icons/export-x-icon.png"}
                            alt=""
                          />
                        </div>
                        <span>{t("export")}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="common-sub-table-wrap add-row">
                <div className="main-body-main-table-wrap position-relative">
                  <table
                    id="tbl_createStockAdjustment"
                    className="common-table first-row-gray w-100"
                  >
                    <thead>
                      <tr>
                        <th>
                          <div className="com-check">
                            <input
                              id="sa_check_all"
                              checked={allSelected}
                              onChange={toggleSelectAll}
                              type="checkbox"
                            />
                            <label
                              htmlFor="sa_check_all"
                              className="com-check-label"
                            />
                          </div>
                        </th>
                        <th>#</th>
                        <th>{t("Img")}</th>
                        {HEAD_COLUMNS.map((col) => {
                          const active = sortConfig.key === col.key;
                          return (
                            <th
                              key={col.key}
                              className={col.sortable ? "cursor-pointer" : ""}
                              style={
                                col.key === "adjust_qty"
                                  ? { width: "100px" }
                                  : undefined
                              }
                              onClick={
                                col.sortable
                                  ? () => handleSort(col.key)
                                  : undefined
                              }
                            >
                              {t(col.label)}
                              {col.sortable ? (
                                <div className="up-down-arrow-btn">
                                  <img
                                    className={
                                      "up-down-arrow-img up-down-arrow-img-forward" +
                                      (active &&
                                      sortConfig.direction === "desc"
                                        ? ""
                                        : " active")
                                    }
                                    src={
                                      base_assets +
                                      "images/icons/up-down-arrow.png"
                                    }
                                    alt=""
                                  />
                                  <img
                                    className={
                                      "up-down-arrow-img up-down-arrow-img-reverse" +
                                      (active &&
                                      sortConfig.direction === "desc"
                                        ? " active"
                                        : "")
                                    }
                                    src={
                                      base_assets +
                                      "images/icons/up-down-arrow-reverse.png"
                                    }
                                    alt=""
                                  />
                                </div>
                              ) : null}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedList.map((result, key) => {
                        const selectable = isRowSelectable(result);
                        const notAdjustable = result?.adjustable === false;
                        return (
                        <tr
                          key={result?._rowKey || key}
                          className={notAdjustable ? "row-inactive" : ""}
                          title={
                            notAdjustable
                              ? result?.reason || t("Not adjustable")
                              : undefined
                          }
                        >
                          <td>
                            <div className="com-check">
                              <input
                                id={result?._rowKey}
                                checked={selecteditem.includes(result?._rowKey)}
                                onChange={() => toggleSelect(result?._rowKey)}
                                disabled={!selectable}
                                title={
                                  !selectable && !notAdjustable
                                    ? t("Adjust Qty must be greater than 0")
                                    : undefined
                                }
                                type="checkbox"
                              />
                              <label
                                htmlFor={result?._rowKey}
                                className="com-check-label"
                              />
                            </div>
                          </td>
                          <td>{key + 1}</td>
                          <td className="table-pro-img">
                            <img
                              src={
                                result?.image
                                  ? result?.image
                                  : "https://gis247.s3.us-east-2.amazonaws.com/placeholder-blank.png"
                              }
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "https://gis247.s3.us-east-2.amazonaws.com/placeholder-blank.png";
                              }}
                              alt=""
                            />
                          </td>
                          <td className="po-no">
                            <div className="tooltip-area-up">
                              <div className="truncate-text-subtable">
                                {result?.product_name ? result?.product_name : ""}
                              </div>
                              {result?.product_name && <span className="tooltip-area-up-text text-white">{result?.product_name}</span>}
                            </div>
                          </td>
                          <td className="po-no">
                            {result?.SKU ? result?.SKU : ""}
                          </td>
                          <td>{result?.stock_id ? result?.stock_id : ""}</td>
                          <td>{result?.metal_name ? result?.metal_name : ""}</td>
                          <td>{result?.stone_name ? result?.stone_name : ""}</td>
                          <td>{result?.size_name ? result?.size_name : ""}</td>
                          <td>{result?.qty ? result?.qty : "0"}</td>
                          <td>
                            <div
                              className="col-input"
                              style={{ justifyContent: "center" }}
                            >
                              <input
                                type="number"
                                min="0"
                                className="qtycheck"
                                name="adjust_qty"
                                style={{ width: "64px" }}
                                value={result?.adjust_qty}
                                disabled={result?.adjustable === false}
                                onFocus={() => clearIfZero(result?._rowKey)}
                                onChange={(e) => {
                                  // Strip a stray leading zero (e.g. typing
                                  // "0" then "1" can leave "01") and force the
                                  // DOM to match immediately — if the parsed
                                  // number is unchanged from state, React
                                  // bails out of re-rendering the input and
                                  // "01" would otherwise stay stuck on screen.
                                  const normalized = e.target.value.replace(
                                    /^0+(?=\d)/,
                                    ""
                                  );
                                  if (normalized !== e.target.value) {
                                    e.target.value = normalized;
                                  }
                                  changeAdjQty(result?._rowKey, normalized);
                                }}
                              />
                            </div>
                          </td>
                          <td>
                            {result?.price_formatted ||
                              toFormatPrice(result?.price, { addSymbol: true })}
                          </td>
                          <td>
                            {toFormatPrice(rowAmount(result), {
                              addSymbol: true,
                            })}
                          </td>
                          <td className="text-center link-color">
                            {result?.location ? result?.location : ""}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} className="main-green-text left-0">
                          {selecteditem.length > 0 ? (
                            <span>
                              {" "}
                              {selecteditem.length} {t("item selected")}
                            </span>
                          ) : (
                            ""
                          )}
                        </td>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td>{totalQty}</td>
                        <td>{totalAdjQty}</td>
                        <td />
                        <td>
                          {toFormatPrice(totalAmount, { addSymbol: true })}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                  {datalodaer && <TableLoader />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <NotesModalLabel setForm_note={setForm_note}></NotesModalLabel>
      <ConfirmationModel
        statement={confirmStatement}
        handler_submit={doSubmit}
      ></ConfirmationModel>
      <button
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#createPurchaseModal"
        id="status_query_select"
      ></button>
    </React.Fragment>
  );
};

export default CreateStockAdjustment;
